/**
 * useEspnDraftBridge
 *
 * Listens for what the JAI browser extension relays from the user's ESPN draft
 * room (extension/draft-hook.js -> background.js -> content.js -> a window
 * message on this page), keeps an ordered pick list, and calls `onPicks` with
 * the full list whenever it changes so the page can hand it to JAI's server.
 *
 * Only draft events for THIS league are used (matched by ESPN league id), so a
 * second draft open in another tab can never leak into the wrong league.
 *
 * Returns what the page needs to explain itself to the user:
 *   extensionVersion   '1.3.0' when the extension is installed (else null)
 *   canRelay           true when that version can relay draft picks (>= 1.3.0)
 *   canReplay          true when it can also replay the draft so far (>= 1.4.0)
 *   connected          the ESPN draft room's live connection is open right now
 *   pickCount          picks the extension has seen
 *   lastEventAt        ms timestamp of the newest event for this league
 *   error              ESPN's own error text, when it refused something
 *   needsResync        the pick list can't be trusted (page opened after the room
 *                      connected, or picks were undone); nothing is sent to JAI's
 *                      server until the ESPN draft tab is reloaded
 */
import { useEffect, useRef, useState } from 'react'
import { newTracker, applyLines, trackerNeedsResync } from '../services/espnDraftTracker'

const MIN_RELAY_VERSION = [1, 3, 0]
// From 1.4.0 the extension keeps the current draft's state and can hand it to a
// JAI tab opened late; older versions can only relay what happens from now on.
const MIN_REPLAY_VERSION = [1, 4, 0]
const PUSH_THROTTLE_MS = 600

const versionAtLeast = (version, min) => {
  const parts = String(version || '').split('.').map((n) => parseInt(n, 10) || 0)
  for (let i = 0; i < min.length; i++) {
    if ((parts[i] || 0) > min[i]) return true
    if ((parts[i] || 0) < min[i]) return false
  }
  return true
}

/** The extension stamps its version on <html> (content.js). */
export const readExtensionVersion = () => {
  try {
    return document.documentElement.dataset.jaiEspnExtension || null
  } catch {
    return null
  }
}

export default function useEspnDraftBridge({ espnLeagueId, enabled, onPicks }) {
  const [status, setStatus] = useState({ connected: false, pickCount: 0, lastEventAt: null, error: null, transport: null, needsResync: false })
  const tracker = useRef(newTracker())
  const lastPushed = useRef('')
  const timer = useRef(null)
  const onPicksRef = useRef(onPicks)
  useEffect(() => { onPicksRef.current = onPicks })

  const extensionVersion = readExtensionVersion()
  const canRelay = versionAtLeast(extensionVersion, MIN_RELAY_VERSION) && !!extensionVersion
  const canReplay = canRelay && versionAtLeast(extensionVersion, MIN_REPLAY_VERSION)

  useEffect(() => {
    if (!enabled || !espnLeagueId) return undefined
    const wanted = String(espnLeagueId)
    tracker.current = newTracker()
    lastPushed.current = ''

    const flush = () => {
      timer.current = null
      const picks = tracker.current.picks
      const key = JSON.stringify(picks)
      if (key === lastPushed.current) return
      lastPushed.current = key
      onPicksRef.current?.(picks)
    }

    const onMessage = (event) => {
      if (event.source !== window) return
      const data = event.data
      if (!data || data.source !== 'jai-extension' || data.type !== 'JAI_DRAFT_EVENT') return
      const ev = data.event
      if (!ev || String(ev.leagueId) !== wanted) return

      if (ev.type === 'frames') {
        // A replay is only for catching up. If live frames have already given
        // this page the full state (an INIT), the replay can only be older than
        // what it already knows (the extension's copy lags by a moment), so it is ignored.
        if (ev.replay && tracker.current.initSeen) return
        tracker.current = applyLines(tracker.current, ev.lines)
        const resync = trackerNeedsResync(tracker.current)
        setStatus((s) => ({ ...s, ...(ev.replay ? {} : { connected: true }), pickCount: tracker.current.picks.length, lastEventAt: Date.now(), error: tracker.current.lastError, needsResync: resync }))
        // A throttle, not a debounce: ESPN sends a clock tick every second, so
        // "wait for quiet" would never fire once the draft is busy (seen in the
        // first live test: pushes stopped at pick 111 of 130). Push at most once
        // per interval, and only when the pick list actually changed.
        // Never while the list cannot be trusted (see trackerNeedsResync): a partial list would overwrite the saved full one.
        if (!resync && !timer.current && JSON.stringify(tracker.current.picks) !== lastPushed.current) {
          timer.current = setTimeout(flush, PUSH_THROTTLE_MS)
        }
      } else if (ev.type === 'connect') {
        setStatus((s) => ({ ...s, transport: ev.transport || null, lastEventAt: Date.now() }))
      } else if (ev.type === 'open') {
        setStatus((s) => ({ ...s, connected: true, lastEventAt: Date.now() }))
      } else if (ev.type === 'close') {
        setStatus((s) => ({ ...s, connected: false, lastEventAt: Date.now() }))
      }
    }

    window.addEventListener('message', onMessage)
    // Opened or reloaded mid-draft? Ask the extension for the draft so far; it
    // answers with ordinary draft events flagged `replay`. (1.3.0 has no replay:
    // the tracker then reports needsResync and the page says to reload ESPN.)
    if (canReplay) {
      try { window.postMessage({ source: 'jai-page', type: 'JAI_DRAFT_REPLAY' }, window.location.origin) } catch { /* nothing to do */ }
    }
    return () => {
      window.removeEventListener('message', onMessage)
      if (timer.current) clearTimeout(timer.current)
    }
  }, [enabled, espnLeagueId, canReplay])

  return { extensionVersion, canRelay, canReplay, ...status }
}
